// =============================================================
// FLOW: Reports workspace — the single Reports screen reading the REAL
// /api/v1/reports/* aggregation (slice 6). A left rail of the six report types,
// a date-range filter, headline metric cards, the detail table, and Excel/CSV +
// PDF export (downloads stream the same endpoint with ?format=).
//
// Bound to @perpetuum/core's ApiClient (report() + reportExportPath()) via the
// useReport hook — no localStorage, no client-side metric math; the server is
// the single source of the numbers.
// =============================================================

import { useMemo, useState } from "react";
import type { ReportFilters, ReportId } from "@perpetuum/core";
import { useReport, useDevices, useCollection } from "../lib/queries.js";
import { useAuth } from "../app/AuthContext.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Btn, ICONS } from "../components/primitives.js";
import { EmptyState } from "../components/EmptyState.js";
import { formatReportValue, formatMetricValue } from "../lib/report-format.js";

const REPORT_IDS: ReportId[] = ["mtbf", "mttr", "downtime", "working-time", "parts-spend", "tasks-repairs"];

export function Reports() {
  const t = useScopedT();
  const { api } = useAuth();
  const [active, setActive] = useState<ReportId>("mtbf");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const filters: ReportFilters = useMemo(() => {
    const f: ReportFilters = {};
    if (from) f.from = new Date(from).toISOString();
    if (to) f.to = new Date(to).toISOString();
    return f;
  }, [from, to]);

  const report = useReport(active, filters);
  const data = report.data;

  // ── id → name resolution (display-only). Devices for any deviceId column;
  // users for userId/assigneeId columns (working-time). Both reuse the existing
  // query hooks — no new endpoints. Each returns null when an id can't resolve,
  // so formatReportValue() can fall back gracefully (short id / "(unknown)").
  const devicesQ = useDevices();
  const usersQ = useCollection("users");
  const deviceName = useMemo(() => {
    const map = new Map((devicesQ.data ?? []).map((d) => [d.id, d.name]));
    return (id: string): string | null => map.get(id) ?? null;
  }, [devicesQ.data]);
  const userName = useMemo(() => {
    const map = new Map((usersQ.data ?? []).map((u) => [u.id, typeof u.name === "string" ? u.name : undefined]));
    return (id: string): string | null => map.get(id) ?? null;
  }, [usersQ.data]);

  // Column header label: known report column keys get a translated, human label;
  // anything unmapped falls back to a Title-Cased version of the raw key.
  const columnLabel = (key: string): string => {
    const label = t(`reports.col.${key}`);
    return label === `reports.col.${key}` ? humanizeKey(key) : label;
  };

  return (
    <div>
      <h2 className="d-h2">{t("reports.title")}</h2>
      <p className="d-sub">{t("reports.sub")}</p>

      <div className="reports-layout" style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        {/* Left rail of report types */}
        <nav className="reports-rail" data-testid="reports-rail" style={{ minWidth: 180 }}>
          {REPORT_IDS.map((id) => (
            <button
              key={id}
              type="button"
              data-testid={`report-${id}`}
              aria-current={active === id}
              className={`reports-rail-item ${active === id ? "is-active" : ""}`}
              onClick={() => setActive(id)}
              style={railItemStyle(active === id)}
            >
              {t(`reports.r.${id}`)}
            </button>
          ))}
        </nav>

        {/* Report body */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="d-card" style={{ marginBottom: 12 }}>
            <div className="d-card-body" style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
              <div className="d-field">
                <label htmlFor="from">{t("reports.from")}</label>
                <div className="field-ctrl">
                  <input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
                </div>
              </div>
              <div className="d-field">
                <label htmlFor="to">{t("reports.to")}</label>
                <div className="field-ctrl">
                  <input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
                </div>
              </div>
              <Btn variant="ghost" size="core" onClick={() => void report.refetch()}>
                {t("reports.run")}
              </Btn>
              <a
                className="btn btn-ghost btn-core"
                href={api.reportExportPath(active, "csv", filters)}
                data-testid="export-csv"
                download
              >
                {t("reports.exportCsv")}
              </a>
              <a
                className="btn btn-ghost btn-core"
                href={api.reportExportPath(active, "pdf", filters)}
                data-testid="export-pdf"
                download
              >
                {t("reports.exportPdf")}
              </a>
            </div>
          </div>

          {report.isLoading && <div className="loading-screen">{t("common.loading")}</div>}

          {data && (
            <>
              {/* Headline metric cards */}
              <div className="report-metrics" data-testid="report-metrics" style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
                {data.metrics.map((m) => {
                  const f = formatMetricValue(m.value, m.unit);
                  return (
                    <div key={m.label} className="d-card metric-card" style={{ minWidth: 140 }}>
                      <div className="d-card-body">
                        <div className="metric-k" style={{ fontSize: 12, opacity: 0.7 }}>
                          {m.label}
                        </div>
                        <div className="metric-v" style={{ fontSize: 22, fontWeight: 700 }}>
                          {f.text}
                          {f.appendUnit && m.unit ? ` ${m.unit}` : ""}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Detail table */}
              <div className="d-card">
                <div className="d-card-body" style={{ overflowX: "auto" }}>
                  <table className="report-table" data-testid="report-table" style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {data.columns.map((c) => (
                          <th key={c} style={cellStyle(true)}>
                            {columnLabel(c)}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.rows.length === 0 && (
                        <tr>
                          <td colSpan={Math.max(1, data.columns.length)} style={{ padding: 0 }}>
                            <EmptyState
                              icon={ICONS.list}
                              title={t("reports.empty")}
                              hint={t("reports.emptyHint")}
                              compact
                            />
                          </td>
                        </tr>
                      )}
                      {data.rows.map((row, ri) => (
                        <tr key={ri}>
                          {row.map((cell, ci) => {
                            const key = data.columns[ci] ?? "";
                            return (
                              <td key={ci} style={cellStyle(false)}>
                                {formatReportValue(key, cell, { deviceName, userName })}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** Title-case a raw column key as a last-resort header (e.g. `downtimeHours` → `Downtime Hours`). */
function humanizeKey(key: string): string {
  const spaced = key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const railItemStyle = (active: boolean) =>
  ({
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: "8px 12px",
    border: 0,
    borderRadius: 6,
    marginBottom: 4,
    cursor: "pointer",
    font: "inherit",
    background: active ? "var(--sys-accent-soft, rgba(0,0,0,0.06))" : "transparent",
    fontWeight: active ? 600 : 400,
    color: "inherit",
  }) as const;

const cellStyle = (head: boolean) =>
  ({
    textAlign: "left",
    padding: "6px 10px",
    borderBottom: "1px solid var(--sys-border, rgba(0,0,0,0.1))",
    fontSize: 13,
    fontWeight: head ? 600 : 400,
    whiteSpace: "nowrap",
  }) as const;
